import type { Museum } from '../data/types';
import schema from './backup.schema.json';
import { toCsv } from './csv';
import { isBezocht, parseKaartInfo, parseStatusMap, vandaag, type KaartInfo, type StatusMap } from './museum';

/** Maximale bestandsgrootte voor terugzetten (bytes): ruim genoeg, maar geen rare bestanden. */
export const MAX_BACKUP_BYTES = 2 * 1024 * 1024;

export interface Backup {
  app: 'museumkompas';
  versie: 1;
  gemaakt: string;
  status: StatusMap;
  kaart: KaartInfo;
}

export function maakBackup(status: StatusMap, kaart: KaartInfo, nu = new Date()): Backup {
  return { app: 'museumkompas', versie: 1, gemaakt: nu.toISOString(), status, kaart };
}

export const backupNaarJson = (b: Backup) => `${JSON.stringify(b, null, 2)}\n`;

/** Wat er in een bestand zit dat je terugzet. */
export interface Terugzetten {
  status: StatusMap;
  /** Alleen een JSON-back-up bevat kaartgegevens; een CSV niet. */
  kaart: KaartInfo | null;
  /** Aantal regels (CSV) of musea (JSON) die niet in de huidige lijst staan. */
  onbekend: number;
  soort: 'json' | 'csv';
}
/** Gelukt: de inhoud. Mislukt: een foutmelding (controleer met `'fout' in r`). */
export type LeesResultaat = Terugzetten | { fout: string };

// ---------- JSON, gecontroleerd met het JSON Schema ----------

type AjvKlasse = new (opts: object) => { compile: (s: object) => ((d: unknown) => boolean) & { errors?: { instancePath: string; message?: string }[] | null } };

/** Ajv is groot en wordt alleen geladen als je echt een bestand terugzet. */
async function laadAjv(): Promise<AjvKlasse> {
  const mod = (await import('ajv')) as unknown as { default: unknown };
  const d = mod.default as { default?: unknown };
  return (d && typeof d === 'object' && 'default' in d ? d.default : mod.default) as AjvKlasse;
}

export async function leesBackupJson(tekst: string, kent: ReadonlySet<string>): Promise<LeesResultaat> {
  let raw: unknown;
  try {
    raw = JSON.parse(tekst);
  } catch {
    return { fout: 'Het bestand is geen geldige JSON.' };
  }
  const Ajv = await laadAjv();
  const valideer = new Ajv({ strict: false }).compile(schema);
  if (!valideer(raw)) {
    const e = valideer.errors?.[0];
    return { fout: `${e?.instancePath || 'bestand'} ${e?.message ?? 'klopt niet'}`.trim() };
  }
  const b = raw as Backup;
  const status = parseStatusMap(b.status) ?? {};
  return {
    status,
    kaart: parseKaartInfo(b.kaart) ?? null,
    onbekend: Object.keys(status).filter((id) => !kent.has(id)).length,
    soort: 'json',
  };
}

// ---------- CSV ----------

const CSV_KOP = ['museum_id', 'museum', 'plaats', 'bezocht', 'bezoeken', 'wensenlijst', 'favoriet'];

/** Alle musea, met je markeringen erbij: ook handig om in Excel of Sheets zelf aan te vullen. */
export function statusNaarCsv(musea: Museum[], status: StatusMap): string {
  const ja = (b: boolean) => (b ? 'ja' : 'nee');
  const rijen = [...musea]
    .sort((a, b) => a.naam.localeCompare(b.naam, 'nl'))
    .map((m) => {
      const s = status[m.id];
      return [m.id, m.naam, m.plaats, ja(isBezocht(s)), (s?.b ?? []).join(' '), ja(!!s?.w), ja(!!s?.f)];
    });
  return toCsv(CSV_KOP, rijen);
}

/** Kleine CSV-lezer: aanhalingstekens, ; , of tab als scheidingsteken, BOM en Windows-regeleinden. */
export function parseCsv(tekst: string): string[][] {
  const t = tekst.replace(/^﻿/, '');
  const eersteRegel = t.split(/\r?\n/, 1)[0] ?? '';
  const scheiding = [';', ',', '\t'].reduce((best, c) => (eersteRegel.split(c).length > eersteRegel.split(best).length ? c : best), ';');
  const rijen: string[][] = [];
  let rij: string[] = [];
  let cel = '';
  let tussen = false;
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (tussen) {
      if (c === '"' && t[i + 1] === '"') {
        cel += '"';
        i++;
      } else if (c === '"') tussen = false;
      else cel += c;
    } else if (c === '"') tussen = true;
    else if (c === scheiding) {
      rij.push(cel);
      cel = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && t[i + 1] === '\n') i++;
      rij.push(cel);
      cel = '';
      if (rij.some((x) => x.trim() !== '')) rijen.push(rij);
      rij = [];
    } else cel += c;
  }
  rij.push(cel);
  if (rij.some((x) => x.trim() !== '')) rijen.push(rij);
  return rijen;
}

const isJa = (s: string | undefined) => /^(ja|yes|y|true|waar|1|x|✓)$/i.test((s ?? '').trim());
const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** Leest een CSV zoals statusNaarCsv die maakt. Regels met een onbekend museum_id worden geteld en overgeslagen. */
export function leesCsv(tekst: string, kent: ReadonlySet<string>, nu = new Date()): LeesResultaat {
  const rijen = parseCsv(tekst);
  if (rijen.length < 2) return { fout: 'Het bestand bevat geen gegevens.' };
  const kop = rijen[0].map((k) => k.trim().toLowerCase());
  const kolom = (naam: string) => kop.indexOf(naam);
  const iId = kolom('museum_id');
  if (iId < 0) return { fout: 'Kolom "museum_id" ontbreekt. Gebruik een CSV die Museumkompas zelf heeft gemaakt.' };
  const iBezocht = kolom('bezocht');
  const iBezoeken = kolom('bezoeken');
  const iWens = kolom('wensenlijst');
  const iFav = kolom('favoriet');

  const status: StatusMap = {};
  let onbekend = 0;
  for (const r of rijen.slice(1)) {
    const id = (r[iId] ?? '').trim();
    if (!id) continue;
    const data = (iBezoeken >= 0 ? (r[iBezoeken] ?? '') : '')
      .split(/[\s,|]+/)
      .map((d) => d.trim())
      .filter((d) => ISO.test(d));
    const bezocht = data.length > 0 || (iBezocht >= 0 && isJa(r[iBezocht]));
    const wens = iWens >= 0 && isJa(r[iWens]);
    const fav = iFav >= 0 && isJa(r[iFav]);
    if (!bezocht && !wens && !fav) continue;
    if (!kent.has(id)) {
      onbekend++;
      continue;
    }
    status[id] = {
      ...(bezocht && { b: data.length ? [...new Set(data)].sort() : [vandaag(nu)] }),
      ...(wens && { w: true as const }),
      ...(fav && { f: true as const }),
    };
  }
  return { status, kaart: null, onbekend, soort: 'csv' };
}

// ---------- Samenvoegen ----------

/** Voegt twee status-lijsten samen: bezoekdata worden verenigd, wensenlijst en favoriet blijven staan als één van beide ze heeft. */
export function voegSamenStatus(huidig: StatusMap, nieuw: StatusMap): StatusMap {
  const uit: StatusMap = { ...huidig };
  for (const [id, n] of Object.entries(nieuw)) {
    const h = uit[id] ?? {};
    const data = [...new Set([...(h.b ?? []), ...(n.b ?? [])])].sort();
    uit[id] = {
      ...(data.length && { b: data }),
      ...((h.w || n.w) && { w: true as const }),
      ...((h.f || n.f) && { f: true as const }),
    };
  }
  return uit;
}

/** Aantallen voor het overzicht vóór het terugzetten. */
export function telStatus(status: StatusMap) {
  const vals = Object.values(status);
  return {
    bezocht: vals.filter((s) => isBezocht(s)).length,
    wens: vals.filter((s) => s.w).length,
    fav: vals.filter((s) => s.f).length,
  };
}
